import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { IRootState } from 'src/reducers';
import PersonnelSearch from '../components/PersonnelSearch';
import useFetch from 'use-http';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import DialogList from 'src/dialogs';
import actions from 'src/actions';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import DefaultPayHistoryDialog from '../dialogs/pay/DefaultPayHistoryDialog';
import { UserRoles } from 'src/models/UserRole';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PersonnelAdvancedQuickFind from '../dialogs/PersonnelAdvancedQuickFindDialog';
import PersonnelToast from '../components/PersonnelToast';
import UpdatePayHistoryDialog from '../dialogs/pay/UpdateDefaultContractPayDialog';
import LandmanPayInfo from 'src/models/personnel/LandmanPayInfo';
import LandmanPayExceptionHistory from 'src/models/personnel/LandmanPayExceptionHistory';
import PayExceptionHistoryDialog from '../dialogs/pay/PayExceptionHistoryDialog';
import LandmanPayException from 'src/models/personnel/LandmanPayException';
import TSDILandman from 'src/models/personnel/TSDILandman';
import Lm1099MaintenanceDialog from '../dialogs/pay/Lm1099MaintenanceDialog';
import LandmanReportDialog from '../dialogs/pay/LandmanReportDialog';
import CompanyMaintenanceDialog from '../../accounts-payable/dialogs/CompanyMaintenanceDialog';

const ContractPayPage = () => {
    const { get, response } = useFetch();
    const { register, errors, setValue } = useForm();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [defaultPayHistory, setDefaultPayHistory] = useState<Array<LandmanPayInfo>>(undefined); // The whole default pay history list
    const [currentDefaultPay, setCurrentDefaultPay] = useState<LandmanPayInfo>(undefined);
    const selectedPersonnel = useSelector((state: IRootState) => state.personnel.selectedPersonnel);

    const [payUnitProp, setPayUnitProp] = useState<string>(undefined); // unit information sent to update dialog
    const [payRateProp, setPayRateProp] = useState<number>(undefined); // rate information sent to update dialog
    
    const [projectPayHistory, setProjectPayHistory] = useState<Array<LandmanPayExceptionHistory>>(undefined); // pay exceptions for a specific project
    const [payExceptions, setPayExceptions] = useState<Array<LandmanPayException>>(undefined); // all pay exceptions

    const dispatch = useDispatch();
    const appContext = useContext<IAppContextState>(AppContext);
    const isUserHR =
        appContext.currentUser.role === UserRoles.HR ||
        appContext.currentUser.role === UserRoles.HRAdmin ||
        appContext.currentUser.role === UserRoles.Admin;

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        if (defaultPayHistory?.length > 0) {
            console.log('====> defaultPayHistory = ', defaultPayHistory);
            let currentDF = defaultPayHistory[0];

            if (currentDF) {
                setCurrentDefaultPay(currentDF);
            } else {
                setCurrentDefaultPay(undefined);
            }
        }
    }, [defaultPayHistory]);

    // resets props for update dialog when current pay rate is updated
    useEffect(() => {
        if (!!currentDefaultPay) {
            setValue('defaultPayRate', currentDefaultPay?.payRate ? parseFloat(currentDefaultPay?.payRate.toString()).toFixed(2) : 0.00);
            setPayRateProp(currentDefaultPay?.payRate ?? undefined);

            setValue('defaultPayUnit', currentDefaultPay?.payUnit ? currentDefaultPay?.payUnit : 'U');
            setPayUnitProp(currentDefaultPay?.payUnit ?? undefined);
        }
    }, [currentDefaultPay]);

    useEffect(() => {
        initializeData();
    }, [selectedPersonnel]);

    const onCreate = () => {
        console.log('ContractPayPage onCreate()');
        initializeData();
    };

    const onDestroy = () => {
        console.log('ContractPayPage onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    // get all information on page for selected personnel
    const initializeData = () => {
        if (!!selectedPersonnel) {
            getAllPayExceptions();
            getDefaultPayHistory();
        }
    };

    // all pay exceptions
    const getAllPayExceptions = async () => {
        let result = await get(`v1.0/landmen/personnel/${selectedPersonnel?.tsdNo}/get-pay-exceptions`);

        if (response.ok) {
            setPayExceptions(result);
        } else {
            showToast('Unable to fetch personnel\'s pay exception information', `rgba(255,0,0,0.5)`);
        }
    };

    // pay exceptions for a selected project
    const getPayHistoryByProject = async (clientNumber: number, projectNumber: number) => {
        setIsLoading(true);
        if (selectedPersonnel?.lmNo !== undefined && selectedPersonnel?.lmNo !== null && selectedPersonnel?.lmNo > 0) {
            let data = await get(`v1.0/landmen/personnel/${selectedPersonnel.lmNo}/clients/${clientNumber}/projects/${projectNumber}/get-project-pay-exceptions`);

            if (response.ok) {
                setProjectPayHistory(data);
                dispatch(actions.layout.showDialog(DialogList.SHOW_PAY_EXCEPTIONS));
            } else {
                showToast('Unable to get selected personnel\'s pay history information for this project', `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Invalid employee number provided', `rgba(255,0,0,0.5)`);
        }

        setIsLoading(false);
    };

    const updateScreenPay = () => {
        showToast('Contract pay update successful');
        getDefaultPayHistory();
    };

    // contract pay history
    const getDefaultPayHistory = async () => {
        setIsLoading(true);
        if (selectedPersonnel?.lmNo !== undefined && selectedPersonnel?.lmNo !== null && selectedPersonnel?.lmNo > 0) {
            let data = await get(`v1.0/landmen/personnel/${selectedPersonnel.lmNo}/get-pay-history`);

            if (response.ok) {
                setDefaultPayHistory(data);
            } else {
                showToast('Unable to get selected personnel\'s pay history information', `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Invalid employee number provided', `rgba(255,0,0,0.5)`);
        }

        setIsLoading(false);
    };

    const displayTitle = () => {
        const title = selectedPersonnel ? `Pay Exceptions - ${selectedPersonnel?.akaName ? selectedPersonnel?.akaName : selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName}` : 'Pay Exceptions';
        return title;
    };

    const showContractPayHistory = async () => {
        if (!!selectedPersonnel) {
            await getDefaultPayHistory();
            dispatch(actions.layout.showDialog(DialogList.DEFAULT_PAY_HISTORY));
        } else if (!selectedPersonnel) {
            showToast('No personnel selected', `rgba(255,0,0,0.5)`);
        }
    };

    const showPersonnelData = () => {
        console.log('Display the Stuffs');
    };

    const showUpdateDialog = async () => {
        if (!!selectedPersonnel) {
            dispatch(actions.layout.showDialog(DialogList.UPDATE_PAY_HISTORY));
        } else if (!selectedPersonnel) {
            showToast('No personnel selected', `rgba(255,0,0,0.5)`);
        }
    };

    const toggleAdvancedQuickFind = () => {
        dispatch(actions.layout.showDialog(DialogList.PERSONNEL_ADVANCED_QUICK_FIND));
    };

    const showLm1099Maintenance = () => {
        dispatch(actions.layout.showDialog(DialogList.LM_1099_MAINTENANCE));
    };

    const showDialog = (dialogName: DialogList) => {
        dispatch(actions.layout.showDialog(dialogName));
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };

     /**
      * Sets the selected Personnel to the store if the given ID is valid and it's not already the same personnel
      * @param personnel - personnel payload
      */
    const setSelectedPersonnel = (personnel: TSDILandman) => {
        if (personnel) {
            personnel?.lmNo !== selectedPersonnel?.lmNo
                ? dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: personnel })
                : showToast('Selected personnel already set', `rgba(78,117,154,0.5)`);

        } else {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
        }
    };

    const displayContractPayExceptions = () => {
        return <form id="personnelContractPayForm">
            <div>
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '4%' }}>Client</th>
                            <th style={{ width: '20%' }}>Client Name</th>
                            <th style={{ width: '4%' }}>Project</th>
                            <th style={{ width: '18%' }}>Project Name</th>
                            <th style={{ width: '10%' }}>Pay Rate</th>
                            <th style={{ width: '10%' }}>Pay Unit</th>
                            <th style={{ width: '10%' }}>Classification</th>
                        </tr>
                    </thead>
                    {payExceptions?.map((exception: LandmanPayException, index: number) => {
                        return (
                            <tbody key={`body-${index}`}>
                                <tr className="table-item-hover" style={{ padding: '1.5rem', textAlign: 'center' }}>
                                    <td style={{ width: '4%' }}>
                                        {exception.clNo}
                                    </td>
                                    <td style={{ width: '20%' }}>
                                        {exception.clientName}
                                    </td>
                                    <td style={{ width: '4%' }}>
                                        {exception.prNo}
                                    </td>
                                    <td style={{ width: '18%' }}>
                                        {exception.projectName}
                                    </td>
                                    <td style={{ width: '10%' }}>
                                        {isUserHR && <RHFInputBox
                                            name={`payExceptions[${index}].payRate`}
                                            type="number"
                                            defaultVal={`${exception.payRate}`}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                            disabled={true}
                                        />}
                                        {!isUserHR && <label style={{ width: '100%' }}>NA</label>}
                                    </td>
                                    <td style={{ width: '10%' }}>
                                        {isUserHR && <RHFSelect
                                            name={`payExceptions[${index}].payUnit`}
                                            defaultVal={exception.payUnit}
                                            options={[
                                                { value: 'D', label: 'Day' },
                                                { value: 'H', label: 'Hour' },
                                                { value: 'M', label: 'Month' },
                                            ]}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                            disabled={true}
                                        />}
                                        {!isUserHR && <label style={{ width: '100%' }}>NA</label>}
                                    </td>
                                    <td style={{ width: '10%' }}>
                                        <RHFInputBox
                                            name={`payExceptions[${index}].classification`}
                                            type="text"
                                            defaultVal={'TBD'}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                            disabled={true}
                                        />
                                    </td>
                                    <td style={{ width: '5%' }}>
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '100%', height: '3rem' }}
                                            text={'History'}
                                            onClick={() => getPayHistoryByProject(exception.clNo, exception.prNo)}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        );
                    })}
                </table>
            </div>
        </form>;
    };

    return (
        <div>
            <PersonnelNavBar />
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <PersonnelSearch showPersonnelData={showPersonnelData} toggleAdvancedQuickFind={toggleAdvancedQuickFind} />
            <Page>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel>
                            <div className="form-row" style={{ textAlign: 'center', verticalAlign: 'middle', margin: '0', padding: '0' }}>
                                {isUserHR && <RHFSelect
                                    name={`defaultPayUnit`}
                                    placeholder="Default Pay Unit"
                                    options={[
                                        { value: 'D', label: 'Day' },
                                        { value: 'H', label: 'Hour' },
                                        { value: 'M', label: 'Month' },
                                        { value: 'Unknown', label: 'Unknown' }
                                    ]}
                                    style={{ width: '100%', border: '.5px solid LightGray', borderRadius: '5px', justifyContent: 'center' }}
                                    bodyStyle={{ width: '25%' }}
                                    register={register}
                                    disabled={true}
                                    errors={errors}
                                    title="Default Pay Unit"
                                    label="Paid By"
                                    labelOnLeft={true}
                                />}
                                {!isUserHR && <RHFSelect
                                    name={`na`}
                                    placeholder="Unauthorized"
                                    options={[
                                        { value: 'D', label: 'Day' },
                                        { value: 'H', label: 'Hour' },
                                        { value: 'M', label: 'Month' },
                                    ]}
                                    style={{ width: '100%', border: '.5px solid LightGray', borderRadius: '5px', justifyContent: 'center' }}
                                    bodyStyle={{ width: '25%' }}
                                    register={register}
                                    errors={errors}
                                    disabled={true}
                                    label="Paid By"
                                    labelOnLeft={true}
                                />}
                                {isUserHR && <RHFInputBox
                                    name={`defaultPayRate`}
                                    placeholder="Default Pay Rate"
                                    type="number"
                                    style={{ width: '100%', border: '.5px solid LightGray', borderRadius: '5px', justifyContent: 'center' }}
                                    bodyStyle={{ width: '25%', marginLeft: '2rem' }}
                                    register={register}
                                    errors={errors}
                                    title="Default Pay Rate"
                                    disabled={true}
                                    label="Pay Rate"
                                    labelOnLeft={true}
                                />}
                                {!isUserHR && <RHFInputBox
                                    name={`na`}
                                    placeholder="Unauthorized"
                                    type="number"
                                    style={{ width: '100%', border: '.5px solid LightGray', borderRadius: '5px', justifyContent: 'center' }}
                                    bodyStyle={{ width: '25%', marginLeft: '2rem' }}
                                    register={register}
                                    errors={errors}
                                    disabled={true}
                                    label="Pay Rate"
                                    labelOnLeft={true}
                                />}
                                <Button
                                    className="blue-hover-button"
                                    style={{whiteSpace: 'nowrap', width: '10%', height: '3rem', marginLeft: '2rem', borderRadius: '5px', justifyContent: 'center' }}
                                    text={'1099 Maint.'}
                                    onClick={() => showLm1099Maintenance()}
                                />
                                <Button
                                    className="blue-hover-button"
                                    style={{ width: '10%', height: '3rem', marginLeft: '1rem', borderRadius: '5px', justifyContent: 'center' }}
                                    intent="primary"
                                    text={'Update'}
                                    onClick={() => showUpdateDialog()}
                                />
                                <Button
                                    className="blue-hover-button"
                                    style={{ width: '10%', height: '3rem', marginLeft: '3rem', borderRadius: '5px', justifyContent: 'center' }}
                                    text={'History'}
                                    disabled={isUserHR ? false : true}
                                    onClick={() => showContractPayHistory()}
                                />
                                <RHFSelect
                                    name={`display`}
                                    placeholder="Display Prospect Staffing"
                                    options={[
                                        { value: 'all', label: 'All' },
                                        { value: 'active', label: 'Active' },
                                        { value: 'inactive', label: 'Inactive' },
                                    ]}
                                    style={{ width: '100%', border: '.5px solid LightGray', borderRadius: '5px', justifyContent: 'center' }}
                                    // onChange={() => filter()}
                                    bodyStyle={{ width: '45%', marginLeft: '2rem' }}
                                    register={register}
                                    errors={errors}
                                    label="Display Prospect Staffing"
                                    labelOnLeft={true}
                                />
                            </div>
                        </Panel>
                    </Panel>
                    <div style={{ height: '.1rem' }} />
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle={displayTitle()}>
                            {payExceptions?.length > 0 && (
                                <div className="medium-panel-with-search" style={{ overflowY: 'scroll', padding: '2rem', paddingTop: '0' }}>
                                    {displayContractPayExceptions()}
                                </div>)}
                            {isLoading && <LoadingSpinner />}
                            {!isLoading && !payExceptions?.length && (
                                <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '20px', marginBottom: '3rem' }}>
                                    No Contract Pay Exception Info
                                </div>)}

                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <DefaultPayHistoryDialog
                dialogName={DialogList.DEFAULT_PAY_HISTORY}
                hideDialog={hideDialog}
                defaultPayHistory={defaultPayHistory}
            />
            <UpdatePayHistoryDialog
                dialogName={DialogList.UPDATE_PAY_HISTORY}
                showToast={showToast}
                hideDialog={hideDialog}
                currentRate={payRateProp ?? 0}
                currentUnit={payUnitProp ?? 'U'}
                selectedPersonnel={selectedPersonnel}
                updateScreenPay={updateScreenPay}
            />
            <PayExceptionHistoryDialog
                dialogName={DialogList.SHOW_PAY_EXCEPTIONS}
                defaultPayHistory={projectPayHistory}
                hideDialog={hideDialog}
            />
            <PersonnelAdvancedQuickFind
                dialogName={DialogList.PERSONNEL_ADVANCED_QUICK_FIND}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel}
                selectPersonnel={setSelectedPersonnel}
            />
            <Lm1099MaintenanceDialog
                dialogName={DialogList.LM_1099_MAINTENANCE}
                hideDialog={hideDialog}
                showDialog={showDialog}
            />
            <LandmanReportDialog
                dialogName={DialogList.LM_1099_REPORT}
                hideDialog={hideDialog}
                showDialog={showDialog}
            />
            <CompanyMaintenanceDialog
                dialogName={DialogList.COMPANY_MAINTENANCE}
                hideDialog={hideDialog}
                showDialog={showDialog}
                backDialog={DialogList.LM_1099_MAINTENANCE}
            />
        </div>
    );
};

export default ContractPayPage;


// WEBPACK FOOTER //
// ./src/components/personnel/pages/ContractPayPage.tsx